import {computed, EventEmitter, inject, Injectable, signal} from '@angular/core';
import {SignalStore} from '@feature-signal-store';
import {CATEGORY_CONFIGS, FilterDefinition, FILTERS_CONFIG, GlobalSearchCategory, GlobalSearchDataStore, GlobalSearchFilterType} from '@global-search/data-access';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {debounceTime, distinctUntilChanged, filter, map, pipe, tap} from 'rxjs';
import {useDevtools} from 'signalstory';
import {IGlobalSearchUiState, initialGlobalSearchUiState} from './global-search-ui.state';
import {IActiveFilterData} from './types';

@Injectable({providedIn: 'root'})
export class GlobalSearchUiStore extends SignalStore<IGlobalSearchUiState> {
	// --- Selectors ---
	readonly isOpen$ = this.select(state => state.isOpen); // Keep if relevant
	readonly searchInput$ = this.select(state => state.searchTerms[state.activeCategory]);
	readonly filters$ = this.select(state => state.filters); // Now this exists!
	readonly activeCategory$ = this.select(state => state.activeCategory);
	public readonly activeCategoryFilters$ = this.select(state => state.filters?.[state.activeCategory] ?? {});
	// Active category filters:
	readonly isFiltersPanelOpen$ = this.select(state => state.isFiltersPanelOpen);
	readonly activeFiltersList$ = computed<IActiveFilterData[]>(() => {
		const category = this.activeCategory$();
		const filters = this.activeCategoryFilters$();
		const definitions = FILTERS_CONFIG[category] ?? []; // Get definitions for labels/types

		return Object.entries(filters).map(([type, value]) => {
			const def = definitions.find(d => d.type === type);
			const label = def ? def.labelKey : type; // Use labelKey or fallback to type
			// Format value for display (can be improved)
			let displayValue = value;
			if (typeof value === 'boolean') {
				displayValue = '';
			} else if (typeof value === 'object' && value !== null && 'label' in value) {
				displayValue = value.label;
			}
			return {
				filter: def,
				type: type as GlobalSearchFilterType,
				label: label, // Needs translation later
				value: value,
				displayValue: displayValue,
			};
		});
	});
	readonly activeFiltersCount$ = computed(() => this.activeFiltersList$().length);
	readonly activeCategoryConfigs$ = computed(() => {
		const category = this.activeCategory$();
		return CATEGORY_CONFIGS[category];
	});
	// --- Sorting selectors ---
	readonly sorting$ = this.select(state => state.sorting);
	readonly activeCategorySorting$ = computed(() => {
		const category = this.activeCategory$();
		return this.sorting$()?.[category] || initialGlobalSearchUiState.sorting[category];
	});
	readonly globalSearchContainerClicked$: EventEmitter<Event> = new EventEmitter<Event>();
	private readonly dataStore = inject(GlobalSearchDataStore);
	// --- Pagination selectors ---
	readonly pageSize$ = computed(() => this.dataStore.getStateForCategory(this.activeCategory$())?.size ?? 5);
	private readonly preventQuery$ = signal(false); // Prevent query if the filter update not allow it
	// Selector combining relevant state for the effect
	private readonly searchTriggerState$ = computed(() => ({
		term: this.searchInput$(),
		category: this.activeCategory$(),
		filters: this.activeCategoryFilters$(),
		sorting: this.activeCategorySorting$(),
		isOpen: this.isOpen$(),
		preventQuery: this.preventQuery$(),
		pageSize: this.pageSize$(),
	}));
	/** ------------------------ Private Methods ----------------------- */
	// Effect to trigger search when the search term, category, filters, or sorting change
	private readonly triggerSearchEffect = rxMethod<{
		term: string;
		category: GlobalSearchCategory;
		filters: Record<string, any>;
		sorting: any;
		preventQuery: boolean;
		isOpen: boolean;
		pageSize: number;
	}>(
		pipe(
			// Use the signal directly
			map(({term, category, filters, sorting, isOpen, preventQuery, pageSize}) => ({
				term,
				category,
				filters,
				sorting,
				isOpen,
				preventQuery,
				pageSize,
			})),
			debounceTime(300),
			// Use distinctUntilChanged on the combined object
			distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
			// Filter ensures we have a category, a valid search term length, AND the panel is open
			filter(({term, category, isOpen, preventQuery}) => !preventQuery && isOpen && !!category && (term.length > 2 || term.length === 0)),
			tap(({term, category, filters, sorting}) => {
				console.log(`UiStore Effect: Triggering search for Cat: ${category}, Term: "${term}", Filters:`, filters, 'Sorting:', sorting);
				// Get the current page size for the category from DataStore state
				const size = this.dataStore.getStateForCategory(category)?.size;
				// Include sorting in the filters object that will be passed to buildQuery
				const filtersWithSorting = {
					...filters,
					__sort: sorting, // Use __sort prefix to avoid collision with filter names
				};
				this.dataStore.fetchSearchResults(category, term, filtersWithSorting, 1, size);
			}),
		),
	);

	/** ------------------------ Constructor ----------------------- */
	constructor() {
		super('Global Search UI', 'Global', initialGlobalSearchUiState, {
			plugins: [useDevtools()],
		});
		// Effect to trigger data fetching when the relevant UI state changes
		this.triggerSearchEffect(this.searchTriggerState$);
		this.initDefaultsOfCategories();
	}

	/** ------------------------ Public Methods ----------------------- */
	loadMore(category: GlobalSearchCategory): void {
		const currentPage = this.dataStore.getStateForCategory(category)?.page ?? 1;
		const size = this.dataStore.getStateForCategory(category)?.size ?? 5;
		this.dataStore.fetchSearchResults(category, this.searchInput$(), this.activeCategoryFilters$(), currentPage + 1, size, false);
	}

	// Setters for UI state
	setSearchInput(searchInput: string): void {
		this.update(state => {
			return {
				...state,
				searchTerms: {
					...state.searchTerms,
					[state.activeCategory]: searchInput,
				},
			};
		}, 'Set Search Input');
		this.preventQuery$.set(false);
	}

	setSelectedCategory(selectedCategory: GlobalSearchCategory): void {
		this.patchState({activeCategory: selectedCategory}, 'Set Selected Category');
	}

	setIsOpen(isOpen: boolean): void {
		this.patchState({isOpen}, 'Set Is Open');
	}

	setPageSize(category: GlobalSearchCategory, pageSize: number) {
		this.dataStore.setPageSize(category, pageSize);
	}

	updateSorting<C extends GlobalSearchCategory>(category: C, sortBy: any, sortDirection: 'asc' | 'desc'): void {
		const sortingUpdate = {
			...this.state().sorting,
			[category]: {
				sortBy,
				sortDirection,
			},
		};

		this.patchState({sorting: sortingUpdate}, `Update Sorting for ${category}`);
	}

	toggleSortDirection<C extends GlobalSearchCategory>(category: C): void {
		const currentSorting = this.activeCategorySorting$();
		const newDirection = currentSorting.sortDirection === 'asc' ? 'desc' : 'asc';

		this.updateSorting(category, currentSorting.sortBy, newDirection);
	}

	updateFilter(payload: {category: GlobalSearchCategory; filter: FilterDefinition<any>; value: any}): void {
		const {category, filter, value} = payload;

		// Call the filter's onValueChanged method to get normalized value and whether search is required
		const {requiresSearch, normalizedValue} = filter.onValueChanged(value);

		// If the filter indicates a search is required, trigger search
		this.preventQuery$.set(!requiresSearch);
		this.updateFilterState(category, filter, normalizedValue);
	}

	resetCategoryFilters(category: GlobalSearchCategory): void {
		this.patchState(state => {
			const filtersUpdate = {...state.filters};
			delete filtersUpdate[category]; // Remove the specific category's filters
			return {filters: filtersUpdate};
		}, 'Reset Category Filters');
	}

	resetAllFilters(): void {
		this.patchState({filters: {}}, 'Reset All Filters');
	}

	removeFilter(payload: {category: GlobalSearchCategory; filter: FilterDefinition<any>}): void {
		// Reuse updateFilter logic by passing undefined as value
		this.updateFilter({...payload, value: undefined});
	}

	toggleFiltersPanel(isOpen?: boolean): void {
		const newState = isOpen !== undefined ? isOpen : !this.isFiltersPanelOpen$();
		this.patchState({isFiltersPanelOpen: newState}, 'Toggle Filters Panel');
	}

	on(event: typeof FilterDefinition.prototype.refetchOn): void {
		switch (event) {
			case 'SelectPanelClosed':
				this.preventQuery$.set(false); // Allow query when select panel is closed
				break;
		}
	}

	/** ----------------------- Signal Factories ----------------------- */
	public isActiveCategorySignalFactory(category: GlobalSearchCategory) {
		return this.select(state => state.activeCategory === category);
	}

	public filterValueSignalFactory(filterType: GlobalSearchFilterType) {
		const definition = FILTERS_CONFIG[this.activeCategory$()]?.find(d => d.type === filterType);
		return this.select(state => {
			if (!state.filters || !state.filters[state.activeCategory]) return definition?.defaultValue;
			const filter = state.filters?.[state.activeCategory]?.[filterType];
			return filter;
		});
	}

	private initDefaultsOfCategories(): void {
		const definitions = FILTERS_CONFIG;
		Object.keys(definitions).forEach((category: GlobalSearchCategory) => {
			const filters = definitions[category];
			Object.entries(filters).forEach(([filterType, filter]) => {
				if (filter.defaultValue !== undefined) {
					this.updateFilterState(category, filter, filter.defaultValue);
				}
			});
		});
	}

	private updateFilterState(category: GlobalSearchCategory, filter: FilterDefinition<any>, value: any): void {
		const currentState = this.state();
		const filtersUpdate = {
			...currentState.filters,
			[category]: {
				...currentState.filters[category], // Keep existing filters
				[filter.type]: value, // Update the specific filter
			},
		};
		// If the filter value is undefined, remove it from the filters object
		if (value === undefined) {
			delete filtersUpdate[category][filter.type];
		}
		// If the category filters are now empty, remove the category key itself
		if (Object.keys(filtersUpdate[category]).length === 0) {
			delete filtersUpdate[category];
		}

		this.update(state => {
			return {
				...state,
				filters: filtersUpdate,
			};
		}, 'Update Filter State');
	}
}
